import { format } from 'date-fns';

import { NeuteredEnum, PetSpeciesEnum } from '@src/components/PatientInformation/PatientInformation.helpers';
import { CustomAttributeKeys } from '@src/constants';
import { AppointmentType } from '@src/lib/__generated/beffe/sdk';
import { Location } from '@src/lib/__generated/vetspire/sdk';
import { ReasonForVisitType } from '@src/store/modules/reason-for-visit.store';
import { findCustomLocationAttribute } from '@src/utils/find-custom-location-attribute';
import { STATE_CODE_BY_NAME, STATE_NAME_BY_CODE } from '@shared-components-web/constants';

const IS_ELIGIBLE_APPOINTMENT_TYPES = new Set<AppointmentType>([
	AppointmentType.Sick,
	AppointmentType.SickUrgent,
	AppointmentType.RoutineCheckup,
]);

const SYMPTOM_QUESTIONNAIRE_CODE_MAP: Record<string, string> = {
	Respiratory: 'nbq_sick_respiratory',
	Gastrointestinal: 'nbq_sick_gastrointestinal',
	'Skin / Dermatological': 'nbq_sick_skin_dermatological',
	'Genital / Urinary': 'nbq_sick_genital_urinary',
	'Muscular / Skeletal': 'nbq_sick_muscular_skeletal',
	'Eyes / Ears': 'nbq_sick_eyes_ears',
	'Mouth / Dental': 'nbq_sick_mouth_dental',
	Neurological: 'nbq_sick_neurological',
	Behavioral: 'nbq_sick_behavioral',
	Other: 'nbq_sick_other',
};

const DURATION_CODE_MAP: Record<string, string> = {
	'24 hours': '24_hours',
	'2-3 days': '2_3_days',
	'4-7 days': '4_7_days',
	'Beyond 7 days': 'beyond_7_days',
	"Don't Recall": 'dont_recall',
};

type SickNoteEntry = { lengthOfIssue?: string; pastIssue?: string; additionalInfo?: string };
type SickNotes = Record<string, SickNoteEntry>;
type RoutineCheckUpNotes = {
	routineCheckUp?: {
		preExistingIssue?: string;
		additionalInfoForPreExistingIssue?: string;
		needsDiscussion?: string;
		additionalInfoForNeedsDiscussion?: string;
	};
};

export type IsNeedsBasedQuestion = {
	questionCode: string;
	answerCodes?: string[];
	text?: string;
};

export type IsNeedsBasedQuestionnaire = {
	questionnaireCode: string;
	questions: IsNeedsBasedQuestion[];
};

export type IsClientInfo = {
	vetspireClientId?: number | null;
	isNewClient: boolean;
};

export type IsPatientInfo = {
	vetspirePatientId?: number | null;
	isNewPatient: boolean;
	birthdate?: string | null;
	isEstimatedBirthdate?: boolean;
	species?: string | null;
	sex?: string | null;
	sterilization?: string | null;
	breed?: string | null;
};

export type IsLovetLocation = {
	locationId: number | null;
	mainLocationId: number | null;
	state: string | null;
};

export type IsPayloadBody = {
	sessionId: string;
	sourceChannel: string;
	client: IsClientInfo | null;
	patient: IsPatientInfo | null;
	lovetLocation: IsLovetLocation;
	needsBasedQuestions: IsNeedsBasedQuestionnaire[] | null;
};

export type IntelligentSchedulingParams = {
	facility: Location;
	appointmentTypes: AppointmentType[];
	sessionGuid: string;
	clientId: string;
	patientId: string;
	mainFacilityId: string | null;
	newClient: boolean | null;
	species: string;
	sex: string;
	neutered: string;
	birthDate: Date | null;
	isEstimatedAge: boolean;
	breed: string;
	mainReasonForVisit: ReasonForVisitType | null;
	notesReasonForVisit: ReasonForVisitType | null;
	notes: Record<string, any>;
};

export type IntelligentSchedulingResult = {
	intelligentSchedulingEnabled: boolean;
	intelligentSchedulingPayload: IsPayloadBody;
};

const parseLovetLocationId = (facilityId: string | null | undefined): number | null => {
	if (!facilityId) return null;
	const numericPart = facilityId.includes('_') ? facilityId.split('_').pop()! : facilityId;
	const parsed = Number.parseInt(numericPart, 10);
	return Number.isNaN(parsed) ? null : parsed;
};

const mapSterilization = (neutered: string): string | null => {
	if (neutered === NeuteredEnum.True) return 'Yes';
	if (neutered === NeuteredEnum.False) return 'No';
	if (neutered === NeuteredEnum.Unknown) return 'Unknown';
	return null;
};

const isFacilityIsEnabled = (facility: Location): boolean => {
	const attrValue = findCustomLocationAttribute(CustomAttributeKeys.intelligentSchedulingEnabled, facility);
	if (!attrValue) return false;
	const normalized = attrValue.trim().toLowerCase();
	return normalized === 'true' || normalized === 'yes';
};

const buildSickNeedsBasedQuestions = (sickNotes: SickNotes): IsNeedsBasedQuestionnaire[] => {
	const questionnaires: IsNeedsBasedQuestionnaire[] = [
		{
			questionnaireCode: 'nbq_reason_for_visit',
			questions: [{ questionCode: 'reason_for_visit', answerCodes: ['sick_or_urgent'] }],
		},
	];

	for (const symptomName of Object.keys(sickNotes)) {
		const questionnaireCode = SYMPTOM_QUESTIONNAIRE_CODE_MAP[symptomName] ?? 'nbq_sick_other';
		const entry = sickNotes[symptomName];
		const questions: IsNeedsBasedQuestion[] = [];

		const durationCode = entry?.lengthOfIssue ? DURATION_CODE_MAP[entry.lengthOfIssue] : undefined;
		if (durationCode) {
			questions.push({ questionCode: 'duration', answerCodes: [durationCode] });
		}

		if (entry?.pastIssue === 'Yes' || entry?.pastIssue === 'No') {
			questions.push({ questionCode: 'happened_before', answerCodes: [entry.pastIssue.toLowerCase()] });
		}

		if (entry?.additionalInfo) {
			questions.push({ questionCode: 'additional_info', answerCodes: [], text: entry.additionalInfo });
		}

		questionnaires.push({ questionnaireCode, questions });
	}

	return questionnaires;
};

const buildRoutineNeedsBasedQuestions = (
	checkup: RoutineCheckUpNotes['routineCheckUp']
): IsNeedsBasedQuestionnaire[] => {
	const preExistingQuestions: IsNeedsBasedQuestion[] = [];
	if (checkup?.preExistingIssue === 'Yes' || checkup?.preExistingIssue === 'No') {
		preExistingQuestions.push({ questionCode: 'answer', answerCodes: [checkup.preExistingIssue.toLowerCase()] });
	}
	if (checkup?.additionalInfoForPreExistingIssue) {
		preExistingQuestions.push({
			questionCode: 'details',
			answerCodes: [],
			text: checkup.additionalInfoForPreExistingIssue,
		});
	}

	const medicalConcernQuestions: IsNeedsBasedQuestion[] = [];
	if (checkup?.needsDiscussion === 'Yes' || checkup?.needsDiscussion === 'No') {
		medicalConcernQuestions.push({ questionCode: 'answer', answerCodes: [checkup.needsDiscussion.toLowerCase()] });
	}
	if (checkup?.additionalInfoForNeedsDiscussion) {
		medicalConcernQuestions.push({
			questionCode: 'details',
			answerCodes: [],
			text: checkup.additionalInfoForNeedsDiscussion,
		});
	}

	return [
		{
			questionnaireCode: 'nbq_reason_for_visit',
			questions: [{ questionCode: 'reason_for_visit', answerCodes: ['routine_check_up'] }],
		},
		{
			questionnaireCode: 'nbq_wellness_pre_existing_conditions',
			questions: preExistingQuestions,
		},
		{
			questionnaireCode: 'nbq_wellness_medical_concerns',
			questions: medicalConcernQuestions,
		},
	];
};

const SPECIES_TO_IS_FORMAT: Record<string, string> = {
	[PetSpeciesEnum.Dog]: 'Dog',
	[PetSpeciesEnum.Cat]: 'Cat',
	[PetSpeciesEnum.Other]: 'Other',
};

export function mapStateToAbbreviation(state: string | null | undefined): string | null {
	if (!state) return null;
	const trimmed = state.trim();
	if (STATE_NAME_BY_CODE[trimmed]) return trimmed;
	return STATE_CODE_BY_NAME[trimmed] ?? null;
}

export function mapSpeciesToIsFormat(species: string | null | undefined): string | null {
	if (!species) return null;
	return SPECIES_TO_IS_FORMAT[species] ?? null;
}

export function buildIntelligentSchedulingPayload({
	facility,
	appointmentTypes,
	sessionGuid,
	clientId,
	patientId,
	mainFacilityId,
	newClient,
	species,
	sex,
	neutered,
	birthDate,
	isEstimatedAge,
	breed,
	notesReasonForVisit,
	notes,
}: IntelligentSchedulingParams): IntelligentSchedulingResult {
	const intelligentSchedulingEnabled =
		isFacilityIsEnabled(facility) &&
		appointmentTypes.some((apptType) => IS_ELIGIBLE_APPOINTMENT_TYPES.has(apptType));

	const clientIdNum = clientId ? Number.parseInt(clientId, 10) : null;
	const patientIdNum = patientId ? Number.parseInt(patientId, 10) : null;

	const locationId = parseLovetLocationId(String(facility.id));
	const mainLocationId = parseLovetLocationId(mainFacilityId) ?? locationId;

	const client: IsClientInfo | null =
		newClient === null
			? null
			: {
					...(clientIdNum !== null && !Number.isNaN(clientIdNum) && { vetspireClientId: clientIdNum }),
					isNewClient: newClient,
			  };

	const patient: IsPatientInfo = {
		...(patientIdNum !== null && !Number.isNaN(patientIdNum) && { vetspirePatientId: patientIdNum }),
		isNewPatient: !patientId,
		birthdate: birthDate ? format(birthDate, 'yyyy-MM-dd') : null,
		isEstimatedBirthdate: isEstimatedAge,
		species: mapSpeciesToIsFormat(species),
		sex: sex || null,
		sterilization: mapSterilization(neutered),
		breed: breed || null,
	};

	const lovetLocation: IsLovetLocation = {
		locationId,
		mainLocationId,
		state: mapStateToAbbreviation(facility.addressFields?.state),
	};

	let needsBasedQuestions: IsNeedsBasedQuestionnaire[] | null = null;
	if (notesReasonForVisit === ReasonForVisitType.SickNeeds) {
		needsBasedQuestions = buildSickNeedsBasedQuestions(notes as SickNotes);
	} else if (notesReasonForVisit === ReasonForVisitType.RoutineCheckUp) {
		needsBasedQuestions = buildRoutineNeedsBasedQuestions((notes as RoutineCheckUpNotes).routineCheckUp);
	}

	return {
		intelligentSchedulingEnabled,
		intelligentSchedulingPayload: {
			sessionId: sessionGuid,
			sourceChannel: 'web',
			client,
			patient,
			lovetLocation,
			needsBasedQuestions,
		},
	};
}
